import { useState } from 'react';
import { Size, t } from '@bms2/shared';
import { Checkbox, Dropdown } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { useCompanySearch } from '../settings/api';
import { post, DEALS_PATH } from './api';
import { useAddNew } from './AddNewHost';
import styles from './RecordPage.module.css';

/** 🔒 Clone a Deal (decision 1482): one route under the Deal it copies. */
export const dealClonePath = (dealId: string) => `${DEALS_PATH}/${dealId}/clone`;

interface CloneDealModalProps {
  open: boolean;
  dealId: string;
  /** The Deal's own Account, which the window opens on. */
  companyId: string | null;
  companyName: string | null;
  apiBaseUrl?: string;
  onCancel: () => void;
  /** The new Deal's id, which the page opens. */
  onCloned: (dealId: string) => void;
}

/**
 * 🔒 The Clone window (piece 18; decision 1482): the Account, opening on the
 * Deal's own, and whether the contacts and the team come along, both ticked.
 * 🔨 Keep contacts greys out with its reason once the Account changes, because
 * a contact is an Employment at the Deal's own Account; the server takes none
 * across an Account change whatever the box said.
 */
export function CloneDealModal({ open, dealId, companyId, companyName, apiBaseUrl = '', onCancel, onCloned }: CloneDealModalProps) {
  const addNew = useAddNew(apiBaseUrl);
  const search = useCompanySearch(apiBaseUrl, 'customer');
  const [account, setAccount] = useState<{ id: string; label: string } | null>(companyId ? { id: companyId, label: companyName ?? '' } : null);
  const [keepContacts, setKeepContacts] = useState(true);
  const [keepTeam, setKeepTeam] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accountChanged = account !== null && account.id !== companyId;
  const save = async () => {
    if (saving) return;
    setError(null);
    setSaving(true);
    const answer = await post<{ id: string }>(dealClonePath(dealId), { companyId: account?.id ?? companyId, keepContacts: keepContacts && !accountChanged, keepTeam }, apiBaseUrl);
    setSaving(false);
    if (!answer.ok) return setError(answer.message);
    onCloned(answer.data.id);
  };
  return (
    <Modal open={open} title={t('deal.clone.title')} onCancel={onCancel} dataModal="deal-clone" actions={[{ kind: 'primary', label: t('deal.clone.confirm'), onAct: () => save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <p className={styles.hint}>{t('deal.clone.explain')}</p>
      <div className={styles.field} data-deal-field="cloneAccount">
        <Dropdown
          chars={32}
          label={t('deal.clone.account')}
          entityLabel={t('deal.clone.accountEntity')}
          search={search}
          value={account?.label ?? null}
          onSelect={(row) => setAccount({ id: row.id, label: row.line1 })}
          onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord', role: 'customer' }, (row) => setAccount({ id: row.id, label: row.line1 }))}
          required
        />
      </div>
      <div data-deal-clone-contacts={accountChanged ? 'greyed' : keepContacts ? 'kept' : 'left'}>
        <Checkbox label={t('deal.clone.keepContacts')} checked={keepContacts && !accountChanged} disabled={accountChanged} size={Size.Small} onChange={setKeepContacts} name="keepContacts" />
        {accountChanged ? <p className={styles.hint} data-deal-clone-contacts-reason="true">{t('deal.clone.contactsElsewhere')}</p> : null}
      </div>
      <Checkbox label={t('deal.clone.keepTeam')} checked={keepTeam} size={Size.Small} onChange={setKeepTeam} name="keepTeam" />
      {addNew.host}
    </Modal>
  );
}
